import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ArrowLeft, Save } from 'lucide-react';
import { useCreateOrder } from '../../hooks/useOrders';
import { useProducts } from '../../hooks/useProducts';
import PageHeader from '../../components/common/PageHeader';

interface OrderFormValues {
  product: string;
  loanNumber: string;
  loanType: string;
  amount: string;
  borrowerFirstName: string;
  borrowerLastName: string;
  propertyAddress: string;
  propertyCity: string;
  propertyState: string;
  propertyZip: string;
}

export default function OrderCreatePage() {
  const navigate = useNavigate();
  const createOrder = useCreateOrder();
  const { data: products, isLoading: loadingProducts } = useProducts();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<OrderFormValues>();

  const onSubmit = (values: OrderFormValues) => {
    createOrder.mutate(
      {
        product: values.product,
        payload: {
          number: values.loanNumber,
          loanType: values.loanType,
          amount: parseFloat(values.amount) || 0,
          borrower: {
            firstName: values.borrowerFirstName,
            lastName: values.borrowerLastName,
          },
          property: {
            address: values.propertyAddress,
            city: values.propertyCity,
            state: values.propertyState,
            zip: values.propertyZip,
          },
        },
      },
      {
        onSuccess: (order) => {
          navigate(`/orders/${order.uuid || order.urn}`);
        },
      },
    );
  };

  return (
    <div>
      <PageHeader
        title="Create Order"
        actions={
          <button onClick={() => navigate('/orders')} className="btn-secondary gap-1">
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
        }
      />

      <form onSubmit={handleSubmit(onSubmit)} className="max-w-2xl space-y-4 lg:space-y-6">
        {/* Product Selection */}
        <div className="card p-4 lg:p-6">
          <h2 className="text-base lg:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 lg:mb-4">Product</h2>
          <div>
            <label className="label">Product *</label>
            <select
              className="input"
              disabled={loadingProducts}
              {...register('product', { required: 'Product is required' })}
            >
              <option value="">Select a product...</option>
              {products?.map((p) => (
                <option key={p.urn} value={p.urn}>
                  {p.productName} ({p.upc})
                </option>
              ))}
            </select>
            {errors.product && (
              <p className="text-sm text-red-500 mt-1">{errors.product.message}</p>
            )}
          </div>
        </div>

        {/* Loan Information */}
        <div className="card p-4 lg:p-6">
          <h2 className="text-base lg:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 lg:mb-4">Loan Information</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Loan Number *</label>
              <input
                type="text"
                className="input"
                {...register('loanNumber', { required: 'Loan number is required' })}
              />
              {errors.loanNumber && (
                <p className="text-sm text-red-500 mt-1">{errors.loanNumber.message}</p>
              )}
            </div>
            <div>
              <label className="label">Loan Type</label>
              <input type="text" className="input" {...register('loanType')} />
            </div>
            <div>
              <label className="label">Amount</label>
              <input
                type="number"
                step="0.01"
                className="input"
                {...register('amount')}
              />
            </div>
          </div>
        </div>

        {/* Borrower */}
        <div className="card p-4 lg:p-6">
          <h2 className="text-base lg:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 lg:mb-4">Borrower</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">First Name *</label>
              <input
                type="text"
                className="input"
                {...register('borrowerFirstName', { required: 'First name is required' })}
              />
              {errors.borrowerFirstName && (
                <p className="text-sm text-red-500 mt-1">{errors.borrowerFirstName.message}</p>
              )}
            </div>
            <div>
              <label className="label">Last Name *</label>
              <input
                type="text"
                className="input"
                {...register('borrowerLastName', { required: 'Last name is required' })}
              />
              {errors.borrowerLastName && (
                <p className="text-sm text-red-500 mt-1">{errors.borrowerLastName.message}</p>
              )}
            </div>
          </div>
        </div>

        {/* Property */}
        <div className="card p-4 lg:p-6">
          <h2 className="text-base lg:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 lg:mb-4">Property</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="label">Address</label>
              <input type="text" className="input" {...register('propertyAddress')} />
            </div>
            <div>
              <label className="label">City</label>
              <input type="text" className="input" {...register('propertyCity')} />
            </div>
            <div>
              <label className="label">State</label>
              <input type="text" className="input" {...register('propertyState')} />
            </div>
            <div>
              <label className="label">ZIP Code</label>
              <input type="text" className="input" {...register('propertyZip')} />
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => navigate('/orders')} className="btn-secondary">
            Cancel
          </button>
          <button type="submit" disabled={createOrder.isPending} className="btn-primary gap-1">
            <Save className="h-4 w-4" />
            {createOrder.isPending ? 'Creating...' : 'Create Order'}
          </button>
        </div>

        {createOrder.isError && (
          <p className="text-sm text-red-500">
            Failed to create order: {(createOrder.error as Error).message}
          </p>
        )}
      </form>
    </div>
  );
}
